import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { lastValueFrom, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { inputPhoneFormValidator } from '@src/app/modules/phone';
import { AUTOCOMPLETE_TYPES } from '@src/constants';
import { AlertService } from '@src/core/services';
import { isFileContact } from '@src/utils';
import { requireContactsValidator } from '@src/app/modules/contacts';

import { RegistrationService } from '../../services';
import { FormDataControls, RegistrationFormContactUI, RegistrationFormUI } from '../../types';

import { PHONE_INITIAL_VALUE } from './constants';

@Component({
  selector: 'registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistrationFormComponent extends BaseResizableComponent implements OnChanges {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly registrationService = inject(RegistrationService);

  @Input() associationId?: string;
  @Input() loading = false;

  @Output() saved = new EventEmitter<RegistrationFormUI>();

  readonly form = new FormGroup<FormDataControls>({
    firstName: new UntypedFormControl(undefined, [Validators.required, Validators.maxLength(50)]),
    middleName: new UntypedFormControl(undefined, [Validators.maxLength(50)]),
    lastName: new UntypedFormControl(undefined, [Validators.required, Validators.maxLength(50)]),
    phone: new UntypedFormControl(PHONE_INITIAL_VALUE, [inputPhoneFormValidator(PHONE_INITIAL_VALUE)]),
    email: new UntypedFormControl(undefined, [Validators.email, Validators.required]),
    contacts: new UntypedFormControl(undefined, requireContactsValidator()),
  });

  readonly autocompleteTypes = AUTOCOMPLETE_TYPES;

  override ngOnInit(): void {
    super.ngOnInit();

    this.registrationService.userContacts$.pipe(takeUntil(this.destroyed$$)).subscribe(contacts => {
      this.form.controls.contacts?.patchValue(contacts);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.associationId && this.associationId) {
      this.registrationService.getUserContactsForUnion(this.associationId);
    }
  }

  async onClickSendButton(): Promise<void> {
    this.loading = true;
    this.form.markAllAsTouched();
    this.form.controls.phone?.updateValueAndValidity();
    this.form.controls.contacts?.updateValueAndValidity();

    if (this.form.invalid) {
      if (this.form.controls.email?.errors?.email) {
        this.alertService.error(this.translateService.instant('common.alerts.errors.emailValidatorError'));
      } else {
        this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      }
      this.loading = false;
      return;
    }

    const formData = await this.mapFormDataToApiRequestType(this.form.getRawValue());

    this.saved.emit(formData);
  }

  private async mapFormDataToApiRequestType(formData: RegistrationFormUI) {
    const { firstName, lastName, middleName, email } = formData;
    let contacts: RegistrationFormContactUI[] = [];

    if (formData.contacts) {
      const promises = formData.contacts.map(async contact => {
        if (isFileContact(contact.contactTypeId)) {
          if (contact.newDocument && this.associationId) {
            contact.value = await this.uploadDocument(contact.newDocument, this.associationId);
          }
        } else {
          contact.value = contact.contact;
        }

        return contact;
      });

      contacts = await Promise.all(promises);
    }

    const phone =
      formData.phone === PHONE_INITIAL_VALUE ? undefined : formData.phone?.replace(new RegExp('[+ -]', 'g'), '');

    return <RegistrationFormUI>{
      parentOrganisationId: this.associationId,
      firstName,
      middleName,
      lastName,
      phone,
      email,
      contacts,
    };
  }

  private async uploadDocument(document: File, parentOrganisationId: string): Promise<string | undefined> {
    if (!document) return;

    const documentId = await lastValueFrom(
      this.registrationService.addDocuments(document, document.name, document.type, parentOrganisationId),
    );

    return documentId.id;
  }
}
